import { useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@legal-500/legal500-design-system";
import { useSearchableSelect } from "./useSearchableSelect";
import type { SelectOption } from "./useSearchableSelect";
import styles from "./SearchableSelect.module.css";

type SearchableSelectProps = {
  name: string;
  label: string;
  required?: boolean;
  value: string | number | null;
  options: SelectOption[];
  onChange: (value: string | number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: string;
  /** When true, keep options in the order supplied by the caller (no label sort). */
  preserveOptionOrder?: boolean;
};

export function SearchableSelect({
  name,
  label,
  required = false,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select an option...",
  helperText,
  error,
  preserveOptionOrder = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
  };

  const {
    searchQuery,
    filteredOptions,
    dropdownStyle,
    highlightedIndex,
    containerRef,
    dropdownRef,
    searchInputRef,
    optionRefs,
    handleSearchChange,
    handleKeyDown,
  } = useSearchableSelect({
    options,
    isOpen,
    onClose: () => setIsOpen(false),
    onSelect: handleSelect,
    preserveOptionOrder,
  });

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption?.label || placeholder;

  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const describedBy = error ? errorId : helperText ? helperId : undefined;

  return (
    <div className={styles.formField}>
      <label htmlFor={name} className={styles.label}>
        {label}{" "}
        {required && (
          <span className={styles.required} aria-label="required">
            *
          </span>
        )}
      </label>

      <div ref={containerRef} className={styles.selectContainer}>
        <button
          type="button"
          id={name}
          className={styles.selectButton}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-describedby={describedBy}
          aria-invalid={!!error}
        >
          <span className={selectedOption ? styles.value : styles.placeholder}>{displayValue}</span>
          <span className={styles.actions}>
            {selectedOption && !disabled && (
              <span
                className={styles.clearButton}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(null);
                }}
                role="button"
                aria-label="Clear selection"
              >
                <Icon icon="cross" size="small" />
              </span>
            )}
            <Icon
              icon={isOpen ? "chevron-up" : "chevron-down"}
              size="small"
              className={styles.arrow}
            />
          </span>
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
            <div className={styles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <ul className={styles.optionsList} role="listbox">
              {filteredOptions.length === 0 ? (
                <li className={styles.noResults}>No results found</li>
              ) : (
                filteredOptions.map((option, index) => (
                  <li
                    key={String(option.value)}
                    ref={el => {
                      if (el) optionRefs.current.set(index, el);
                    }}
                    className={`${styles.option} ${
                      option.value === value ? styles.optionSelected : ""
                    } ${index === highlightedIndex ? styles.optionHighlighted : ""} ${
                      option.disabled ? styles.optionDisabled : ""
                    }`}
                    onClick={() => handleSelect(option)}
                    role="option"
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled || undefined}
                  >
                    <div className={styles.optionContent}>
                      <span className={styles.optionLabel}>{option.label}</span>
                      {option.description && (
                        <span className={styles.optionDescription}>{option.description}</span>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body,
        )}

      {error && (
        <span id={errorId} className={styles.errorText} role="alert" aria-live="polite">
          {error}
        </span>
      )}
      {helperText && !error && (
        <span id={helperId} className={styles.helperText}>
          {helperText}
        </span>
      )}
    </div>
  );
}
